import type { DepartmentTreeNode } from "@evolua/api-client";
import {
  Badge,
  Card,
  DescriptionList,
  EmptyState,
  ErrorState,
  FreshnessBadge,
  PageHeader,
  Skeleton,
  Tabs,
  badgeToneForStatus,
} from "@evolua/ui";
import { useMemo, useState, type ReactNode } from "react";
import { useCompanies, useDepartmentTree, usePositions } from "../api/use-org.js";
import { useSession } from "../session/SessionContext.js";

type TabId = "empresas" | "departamentos" | "cargos";

const TABS: Array<{ id: TabId; label: string }> = [
  { id: "empresas", label: "Empresas" },
  { id: "departamentos", label: "Departamentos" },
  { id: "cargos", label: "Cargos" },
];

function DeptTree({
  nodes,
  selectedId,
  onSelect,
  depth = 0,
}: {
  nodes: DepartmentTreeNode[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  depth?: number;
}) {
  if (!nodes.length) return null;
  return (
    <ul className="evolua-org-tree">
      {nodes.map((node) => {
        const open = selectedId === node.id || hasSelectedDescendant(node, selectedId);
        return (
          <li key={node.id}>
            <button
              type="button"
              className={
                selectedId === node.id
                  ? "evolua-org-tree__node evolua-org-tree__node--active"
                  : "evolua-org-tree__node"
              }
              onClick={() => onSelect(node.id)}
            >
              <span>{node.name}</span>
              {node.code ? <Badge tone="neutral">{node.code}</Badge> : null}
              <span className="evolua-org-tree__count">{node.employeeCount}</span>
            </button>
            {open && node.children.length > 0 ? (
              <DeptTree
                nodes={node.children}
                selectedId={selectedId}
                onSelect={onSelect}
                depth={depth + 1}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function hasSelectedDescendant(
  node: DepartmentTreeNode,
  selectedId: string | null,
): boolean {
  if (!selectedId) return true;
  return node.children.some(
    (c) => c.id === selectedId || hasSelectedDescendant(c, selectedId),
  );
}

function Panel({ title, children }: { title: string; children: ReactNode }) {
  return <Card title={title}>{children}</Card>;
}

/** Gestão de empresas, departamentos e cargos (FASE-06-T29). */
export function OrgStructurePage() {
  const { isDemoMode } = useSession();
  const [tab, setTab] = useState<TabId>("empresas");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string | null>(null);
  const [selectedDeptId, setSelectedDeptId] = useState<string | null>(null);
  const [selectedPositionId, setSelectedPositionId] = useState<string | null>(null);

  const companies = useCompanies();
  const tree = useDepartmentTree();
  const positions = usePositions();

  const selectedCompany = useMemo(
    () => companies.data?.data.find((c) => c.id === selectedCompanyId) ?? null,
    [companies.data, selectedCompanyId],
  );

  const selectedDept = useMemo(() => {
    if (!selectedDeptId || !tree.data) return null;
    const find = (nodes: DepartmentTreeNode[]): DepartmentTreeNode | null => {
      for (const n of nodes) {
        if (n.id === selectedDeptId) return n;
        const child = find(n.children);
        if (child) return child;
      }
      return null;
    };
    return find(tree.data.data);
  }, [selectedDeptId, tree.data]);

  const selectedPosition = useMemo(
    () => positions.data?.data.find((p) => p.id === selectedPositionId) ?? null,
    [positions.data, selectedPositionId],
  );

  const loading = companies.isPending || tree.isPending || positions.isPending;
  const errored = companies.isError || tree.isError || positions.isError;

  return (
    <>
      <PageHeader
        title="Estrutura organizacional"
        lead={`Empresas, hierarquia de departamentos e cargos com contagem de colaboradores${isDemoMode ? " (dados demo)." : "."}`}
      />

      <Tabs items={TABS} value={tab} onChange={(id) => setTab(id as TabId)}>
        {loading ? (
          <div className="evolua-stack evolua-stack--2" aria-busy="true">
            <Skeleton height="2rem" label="Carregando estrutura" />
            <Skeleton height="8rem" />
          </div>
        ) : null}

        {errored ? (
          <ErrorState
            onRetry={() => {
              void companies.refetch();
              void tree.refetch();
              void positions.refetch();
            }}
          />
        ) : null}

        {!loading && !errored ? (
          <>
            {tab === "empresas" ? (
              <div className="evolua-org-layout">
                <div className="evolua-org-list">
                  {(companies.data?.data ?? []).map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      className={
                        selectedCompanyId === c.id
                          ? "evolua-org-list__item evolua-org-list__item--active"
                          : "evolua-org-list__item"
                      }
                      onClick={() => setSelectedCompanyId(c.id)}
                    >
                      <span className="evolua-org-list__name">
                        {c.tradeName ?? c.legalName}
                      </span>
                      <span className="evolua-org-list__meta">
                        {c.employeeCount} colaborador{c.employeeCount === 1 ? "" : "es"}
                      </span>
                    </button>
                  ))}
                  {!companies.data?.data.length ? (
                    <EmptyState title="Nenhuma empresa" description="Sincronize o ERP." />
                  ) : null}
                </div>
                <Panel
                  title={
                    selectedCompany
                      ? (selectedCompany.tradeName ?? selectedCompany.legalName)
                      : "Detalhe"
                  }
                >
                  {selectedCompany ? (
                    <DescriptionList
                      items={[
                        { term: "Razão social", description: selectedCompany.legalName },
                        { term: "CNPJ", description: selectedCompany.cnpj ?? "—" },
                        {
                          term: "Colaboradores",
                          description: selectedCompany.employeeCount,
                        },
                        {
                          term: "Sincronização",
                          description: (
                            <FreshnessBadge
                              syncedAt={
                                selectedCompany.syncedAt
                                  ? new Date(selectedCompany.syncedAt)
                                  : null
                              }
                            />
                          ),
                        },
                        {
                          term: "Integração que alimenta",
                          description:
                            selectedCompany.integrations.length === 0 ? (
                              "Nenhuma integração vinculada"
                            ) : (
                              <ul className="evolua-plain-list">
                                {selectedCompany.integrations.map((i) => (
                                  <li key={i.id}>
                                    <Badge tone="brand">{i.provider}</Badge>{" "}
                                    <Badge tone={badgeToneForStatus(i.status)}>
                                      {i.status}
                                    </Badge>
                                    {i.baseUrl ? (
                                      <span className="evolua-muted">{i.baseUrl}</span>
                                    ) : null}
                                  </li>
                                ))}
                              </ul>
                            ),
                        },
                      ]}
                    />
                  ) : (
                    <p className="evolua-muted">
                      Selecione uma empresa para ver o vínculo com a integração.
                    </p>
                  )}
                </Panel>
              </div>
            ) : null}

            {tab === "departamentos" ? (
              <div className="evolua-org-layout">
                <div className="evolua-org-list">
                  <DeptTree
                    nodes={tree.data?.data ?? []}
                    selectedId={selectedDeptId}
                    onSelect={setSelectedDeptId}
                  />
                  {!tree.data?.data.length ? (
                    <EmptyState
                      title="Sem departamentos"
                      description="Aguarde a sincronização da estrutura."
                    />
                  ) : null}
                </div>
                <Panel title={selectedDept?.name ?? "Detalhe"}>
                  {selectedDept ? (
                    <DescriptionList
                      items={[
                        { term: "Código", description: selectedDept.code ?? "—" },
                        {
                          term: "Colaboradores neste nó",
                          description: selectedDept.employeeCount,
                        },
                        {
                          term: "Subdepartamentos",
                          description: selectedDept.children.length,
                        },
                      ]}
                    />
                  ) : (
                    <p className="evolua-muted">
                      Expanda a árvore e selecione um departamento.
                    </p>
                  )}
                </Panel>
              </div>
            ) : null}

            {tab === "cargos" ? (
              <div className="evolua-org-layout">
                <div className="evolua-org-list">
                  {(positions.data?.data ?? []).map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      className={
                        selectedPositionId === p.id
                          ? "evolua-org-list__item evolua-org-list__item--active"
                          : "evolua-org-list__item"
                      }
                      onClick={() => setSelectedPositionId(p.id)}
                    >
                      <span className="evolua-org-list__name">{p.name}</span>
                      <span className="evolua-org-list__meta">
                        {p.employeeCount} colaborador{p.employeeCount === 1 ? "" : "es"}
                      </span>
                    </button>
                  ))}
                </div>
                <Panel title={selectedPosition?.name ?? "Detalhe"}>
                  {selectedPosition ? (
                    <DescriptionList
                      items={[
                        { term: "Código", description: selectedPosition.code ?? "—" },
                        { term: "CBO", description: selectedPosition.cbo ?? "—" },
                        {
                          term: "Colaboradores",
                          description: selectedPosition.employeeCount,
                        },
                        {
                          term: "Sincronização",
                          description: (
                            <FreshnessBadge
                              syncedAt={
                                selectedPosition.syncedAt
                                  ? new Date(selectedPosition.syncedAt)
                                  : null
                              }
                            />
                          ),
                        },
                      ]}
                    />
                  ) : (
                    <p className="evolua-muted">Selecione um cargo.</p>
                  )}
                </Panel>
              </div>
            ) : null}
          </>
        ) : null}
      </Tabs>
    </>
  );
}
